<template>
    <div class="list-aktiviteter">
        <KHeader heading="Aktiviteter">
            <KButton variant="hollow" size="slim" :icon-path="PlusCircleBlue" @click="() => addAktivitet()">
                Lägg till aktivitet
            </KButton>
        </KHeader>

        <KFilter
            name="aktiviteter-filter"
            :searchPlaceholder="searchPlaceholder"
            :schema="filterSchema"
            :defaultValue="defaultFilterValues"
            @submit="onFilter"
        />

        <KTable
            class="table-aktiviteter"
            paginator
            alwaysShowPaginator
            name="aktiviteter-table"
            :columns="columns"
            :actions="tableActions"
            :value="rows"
            :totalRecords="count"
            :loading="isLoading"
            :first="first"
            @page="onPage"
            @sort="onSort"
        />
    </div>
</template>

<script lang="ts">
    import tabIcon from "@/assets/narvarostatistik.svg";

    export default {
        name: "ListAktiviteter",
        tabName: "Aktiviteter",
        icon: tabIcon,
        refreshTab: true,
    };
</script>

<script setup lang="ts">
    // @ts-nocheck
    import { computed, onBeforeUnmount, onMounted, ref, toRefs, watch } from "vue";
    import moment from "moment";
    import emitter from "tiny-emitter/instance";
    import { openDialog } from "@/utils";
    import { getErrorMessage } from "@/api";
    import dialogEvents from "@/dialogEvents";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import KButton from "@/components/Button.vue";
    import KHeader from "@/components/KHeader.vue";
    import PlusCircleBlue from "@/assets/plus_circle_blue.svg";
    import FormAktivitet from "@/tabs/Skreg/Aktiviter/FormAktivitet.vue";
    import { useStore } from "@/store";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";

    const store = useStore();

    const props = defineProps<{ tabId?: number }>();
    const { tabId } = toRefs(props);

    const searchPlaceholder = "Sök aktivitet, lag, typ, plats, underlag, beskrivning";

    const getDefaultFilterParams = () => ({
        tidpunkt__date__gte: moment().subtract(10, "days").format("YYYY-MM-DD"),
        tidpunkt__date__lte: moment().add(10, "days").format("YYYY-MM-DD"),
    });

    const defaultFilterValues = getDefaultFilterParams();

    const isLoading = ref(false);
    const filterParams = ref({ ...defaultFilterValues });
    const subsetName = ref("default");
    const page = ref(1);
    const limit = ref(20);
    // Antalet rader som finns för subsetet, som paginatorn räknar sidor på
    const count = computed(() =>
        store.getters["skreg/aktiviteter/aktiviteterSubsetTotal"]({
            subsetName: subsetName.value,
        })
    );

    const first = computed(() => (page.value - 1) * limit.value);

    const newlyCreated = computed(() =>
        store.getters["skreg/aktiviteter/aktiviteterSubset"]({
            subsetName: "newlyCreated",
            offset: 0,
            limit: 0,
            toArray: false,
        })
    );
    const newlyUpdated = computed(() =>
        store.getters["skreg/aktiviteter/aktiviteterSubset"]({
            subsetName: "newlyUpdated",
            offset: 0,
            limit: 0,
            toArray: false,
        })
    );

    watch([() => newlyCreated.value.size, () => newlyUpdated.value.size], () => {
        fetchList();
    });

    // Ett filterfält per listkolumn. FK-kolumnerna använder de delade autocompletes (skickar id),
    // datum/tid är intervall på tidpunkt, status matchar backend-property-filtret.
    const filterSchema = generateSchema({
        addToAll: { ignoreEmptyData: true },
        schema: [
            { component: "LagAutocomplete", name: "lag", label: "Lag" },
            { component: "PlatsAutocomplete", name: "plats", label: "Plats" },
            { component: "KDateTimepicker", name: "tidpunkt__date__gte", label: "Från datum" },
            { component: "KDateTimepicker", name: "tidpunkt__date__lte", label: "Till datum" },
            { component: "KDateTimepicker", name: "tidpunkt__time__gte", label: "Från tid", type: "time" },
            { component: "KDateTimepicker", name: "tidpunkt__time__lte", label: "Till tid", type: "time" },
            { component: "KNumber", name: "langd", label: "Längd i minuter" },
            { component: "AktivitetstypAutocomplete", name: "aktivitetstyp", label: "Typ av aktivitet" },
            { component: "UnderlagAutocomplete", name: "underlag", label: "Underlag" },
            { component: "KText", name: "beskrivning", label: "Beskrivning" },
            {
                component: "KAutocomplete",
                name: "status",
                label: "Status",
                options: [
                    { value: "Ej avstämd", label: "Ej avstämd" },
                    { value: "Avstämd", label: "Avstämd" },
                ],
            },
        ],
    });

    // Datum och Tid sorterar båda på tidpunkt (datum först, tid sedan). sortField är det
    // ordering-param backend förstår (se AktivitetFilter); field är fältet som visas i cellen.
    const columns = [
        { header: "Lag", field: "_lag", sortField: "lag__namn", sortable: true },
        { header: "Plats", field: "_plats", sortField: "plats__namn", sortable: true },
        { header: "Datum", field: "datum", sortField: "tidpunkt", sortable: true },
        { header: "Tid", field: "_tid", sortField: "tidpunkt", sortable: true },
        { header: "Längd", field: "langd", sortField: "langd", sortable: true },
        { header: "Typ av aktivitet", field: "_aktivitetstyp", sortField: "aktivitetstyp__namn", sortable: true },
        { header: "Underlag", field: "_underlag", sortField: "underlag__namn", sortable: true },
        { header: "Beskrivning", field: "beskrivning", sortField: "beskrivning", sortable: true },
        { header: "Status", field: "status", sortField: "status", sortable: true },
    ];

    const rows = computed(() =>
        store.getters["skreg/aktiviteter/aktiviteterSubset"]({
            subsetName: subsetName.value,
            offset: first.value,
            limit: limit.value,
        }).map((item) => ({
            ...item,
            _lag: item.lag_display?.namn ?? "",
            _plats: item.plats_display?.namn ?? "",
            _aktivitetstyp: item.aktivitetstyp_display?.namn ?? "",
            _underlag: item.underlag_display?.namn ?? "",
            _tid: item.tid?.slice(0, 5) ?? "",
            tableInfo: {
                isNewlyCreated: !!newlyCreated.value.get(item.id),
                isNewlyUpdated: !!newlyUpdated.value.get(item.id),
            },
        }))
    );

    const tableActions = [
        {
            title: "Stäm av närvaro",
            show: () => true,
            callback: ({ data }) => openAktivitet(data),
        },
        {
            title: "Radera aktivitet",
            show: () => true,
            callback: ({ data }) => deleteAktivitetDialog(data),
        },
        {
            title: "Duplicera aktivitet",
            show: () => true,
            callback: ({ data }) => duplicateAktivitet(data),
        },
    ];

    const fetchList = async ({ forceRefresh = false } = {}) => {
        isLoading.value = true;
        try {
            const { subsetName: fetchedSubset } = await store.dispatch("skreg/aktiviteter/fetchAktiviteter", {
                page: page.value,
                limit: limit.value,
                filterParams: filterParams.value,
                forceRefresh,
            });
            subsetName.value = fetchedSubset;
        } catch {
            openDialog("Kunde inte hämta aktiviteter.", "warning");
        } finally {
            isLoading.value = false;
        }
    };

    const onFilter = ({ formData }) => {
        const data = { ...formData };
        for (const key in data) {
            if (!data[key] || data[key].length === 0) delete data[key];
        }
        // Behåll aktiv sortering när filtret ändras
        if (filterParams.value.ordering) data.ordering = filterParams.value.ordering;
        filterParams.value = data;
        page.value = 1;
        fetchList();
    };

    const onSort = ({ sortField, sortOrder }) => {
        const params = { ...filterParams.value };
        if (sortField && sortOrder) {
            params.ordering = sortOrder === -1 ? `-${sortField}` : sortField;
        } else {
            delete params.ordering;
        }
        filterParams.value = params;
        page.value = 1;
        fetchList();
    };

    const onPage = ({ page: newPage, rows: newRows }) => {
        page.value = newPage;
        limit.value = newRows;
        fetchList();
    };

    const addAktivitet = async () => {
        await store.dispatch("tabs/closeTab", FormAktivitet);
        await store.dispatch("tabs/openTab", { component: FormAktivitet });
    };

    const openAktivitet = async (aktivitet) => {
        await store.dispatch("tabs/closeTab", FormAktivitet);
        await store.dispatch("tabs/openTab", { component: FormAktivitet, parameters: { aktivitet } });
    };

    const duplicateAktivitet = async (aktivitet) => {
        await store.dispatch("tabs/closeTab", FormAktivitet);
        await store.dispatch("tabs/openTab", {
            component: FormAktivitet,
            parameters: { aktivitet, duplicate: true },
        });
    };

    const fetchAvstamningar = async (aktivitet) => {
        try {
            return await store.dispatch("skreg/aktiviteter/fetchAvstamningar", { aktivitetId: aktivitet.id });
        } catch (e) {
            openDialog(getErrorMessage(e), "error");
            return [];
        }
    };

    const deleteAktivitet = async (aktivitet) => {
        await store.dispatch("skreg/aktiviteter/deleteAktivitet", { aktivitet });
        await fetchList({ forceRefresh: true });
    };

    const deleteAktivitetDialog = (aktivitet) => {
        if (aktivitet.status === "Avstämd") {
            openDialog("Denna aktivitet är avstämd och kan inte raderas.", "error");
            return;
        }

        dialogEvents.$emit("open", {
            type: "warning",
            description: "Vill du verkligen radera denna aktivitet?",
            buttons: [
                { title: "Nej", type: "primary" },
                {
                    title: "Ja",
                    type: "secondary",
                    action: async () => {
                        const avstamningar = await fetchAvstamningar(aktivitet);

                        if (avstamningar.some((a) => a.journalanteckning)) {
                            openDialog(
                                "Denna aktivitet innehåller kopplingar till skadekort och kan inte raderas.",
                                "error"
                            );
                            return;
                        }

                        // Om spelare redan är avstämda förlorar man registrerad närvaro vid radering
                        if (avstamningar.some((a) => a.status !== "Ej avstämd")) {
                            dialogEvents.$emit("open", {
                                type: "warning",
                                description:
                                    "Du kommer förlora registrerad närvaro på denna aktivitet. Vill du radera ändå?",
                                buttons: [
                                    { title: "Nej", type: "primary" },
                                    { title: "Ja", type: "secondary", action: () => deleteAktivitet(aktivitet) },
                                ],
                            });
                        } else {
                            await deleteAktivitet(aktivitet);
                        }
                    },
                },
            ],
        });
    };

    // Tabbens uppdatera-knapp (refreshTab) hämtar färsk data förbi subset-cachen
    const onRefreshTab = () => fetchList({ forceRefresh: true });

    onMounted(async () => {
        await store.dispatch("skreg/aktiviteter/addSubscriber");
        emitter.on(`refreshTab-${tabId.value}`, onRefreshTab);
        await fetchList();
    });

    onBeforeUnmount(async () => {
        emitter.off(`refreshTab-${tabId.value}`, onRefreshTab);
        await store.dispatch("skreg/aktiviteter/removeSubscriber");
    });
</script>

<style lang="scss" scoped>
    .list-aktiviteter {
        .table-aktiviteter {
            width: 100%;
        }
    }
</style>
